import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AutolineSettings } from '../../../../core/api/models';
import ErrorMessage from '../../../../components/text/ErrorMessage';
import validator from 'validator';
import { DropdownList } from 'react-widgets';
import { useMasterData } from '../../../../core/hooks/useMasterData';
import api from '../../../../api';

const UpdateForm: React.FunctionComponent<{
  onSubmit: (data: AutolineSettings) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: AutolineSettings;
  error?: string;
}> = (props) => {
  const masterData = useMasterData(api.settings);

  const { control, handleSubmit, errors, watch } = useForm<AutolineSettings>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldUnregister: true,
    defaultValues: props.data,
  });

  const unique = [
    ...new Set(
      masterData.data?.vehicleVariantCombinations
        ?.filter((vv) => vv.isActive)
        .map((item) => item.model.year)
    ),
  ].sort();

  const years =
    unique?.map((x) => {
      return {
        _id: x,
        name: x,
      };
    }) || [];

  const renderApiKey = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">API Key</label>
          <Controller
            control={control}
            rules={{
              required: 'API Key is required',
            }}
            name="apiKey"
            defaultValue={props.data?.apiKey}
            render={({ onChange, value, name }) => (
              <input
                type="password"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="API Key"
                name={name}
                value={value}
                onChange={onChange}
                id="apiKey"
              />
            )}
          />
          <ErrorMessage>{errors.apiKey?.message}</ErrorMessage>
        </div>
      </div>
    );
  };
  const renderDefaultSalesmanHrSystemId = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Default Salesman HR System ID
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Default Salesman HR System ID is required',
            }}
            name="defaultSalesmanHrSystemId"
            defaultValue={props.data?.defaultSalesmanHrSystemId}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Default Salesman HR System ID"
                name={name}
                value={value}
                onChange={onChange}
                id="defaultSalesmanHrSystemId"
              />
            )}
          />
          <ErrorMessage>
            {errors.defaultSalesmanHrSystemId?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderDefaultSourceOfBusiness = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Default Source Of Business
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Default Source Of Business is required',
            }}
            name="defaultSourceOfBusiness"
            defaultValue={props.data?.defaultSourceOfBusiness}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Default Source Of Business"
                name={name}
                value={value}
                onChange={onChange}
                id="defaultSourceOfBusiness"
              />
            )}
          />
          <ErrorMessage>{errors.defaultSourceOfBusiness?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCampaignDiscountAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Campaign Discount Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Campaign Discount Autoline Code is required',
            }}
            name="campaignDiscountAutolineCode"
            defaultValue={props.data?.campaignDiscountAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Campaign Discount Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="campaignDiscountAutolineCode"
              />
            )}
          />
          <ErrorMessage>
            {errors.campaignDiscountAutolineCode?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderExtraAddOnMenuCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Extra AddOn Autoline Code
          </label>
          <Controller
            control={control}
            name="extraAddOnMenuCode"
            defaultValue={props.data?.extraAddOnMenuCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Extra AddOn Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="extraAddOnMenuCode"
              />
            )}
          />
        </div>
      </div>
    );
  };

  const renderAccessoriesDiscountAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Accessories Discount Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Accessories Discount Autoline Code is required',
            }}
            name="accessoriesDiscountAutolineCode"
            defaultValue={props.data?.accessoriesDiscountAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Accessories Discount Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="accessoriesDiscountAutolineCode"
              />
            )}
          />
          <ErrorMessage>
            {errors.accessoriesDiscountAutolineCode?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCashBackDiscountAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            CashBack Discount Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'CashBack Discount Autoline Code is required',
            }}
            name="cashBackDiscountAutolineCode"
            defaultValue={props.data?.cashBackDiscountAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="CashBack Discount Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="cashBackDiscountAutolineCode"
              />
            )}
          />
          <ErrorMessage>
            {errors.cashBackDiscountAutolineCode?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCouponDiscountAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Coupon Discount Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Coupon Discount Autoline Code is required',
            }}
            name="couponDiscountAutolineCode"
            defaultValue={props.data?.couponDiscountAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Coupon Discount Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="couponDiscountAutolineCode"
              />
            )}
          />
          <ErrorMessage>
            {errors.couponDiscountAutolineCode?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderVinDiscountAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Vin Discount Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Vin Discount Autoline Code is required',
            }}
            name="vinDiscountAutolineCode"
            defaultValue={props.data?.vinDiscountAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Vin Discount Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="vinDiscountAutolineCode"
              />
            )}
          />
          <ErrorMessage>{errors.vinDiscountAutolineCode?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderAdjustmentsAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Adjustments Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Adjustments Autoline Code is required',
            }}
            name="adjustmentsAutolineCode"
            defaultValue={props.data?.adjustmentsAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Adjustments Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="adjustmentsAutolineCode"
              />
            )}
          />
          <ErrorMessage>{errors.adjustmentsAutolineCode?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderListingPriceAutolineCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Listing Price Autoline Code
          </label>
          <Controller
            control={control}
            rules={{
              required: 'Listing Price Autoline Code is required',
            }}
            name="listingPriceAutolineCode"
            defaultValue={props.data?.listingPriceAutolineCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Listing Price Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="listingPriceAutolineCode"
              />
            )}
          />
          <ErrorMessage>
            {errors.listingPriceAutolineCode?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderFinanceCommissionMenuCode = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Finance Commission Autoline Code
          </label>
          <Controller
            control={control}
            // rules={{
            //   required: 'Finance Commission Autoline Code is required',
            // }}
            name="financeCommissionMenuCode"
            defaultValue={props.data?.financeCommissionMenuCode}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Finance Commission Autoline Code"
                name={name}
                value={value}
                onChange={onChange}
                id="financeCommissionMenuCode"
              />
            )}
          />
          <ErrorMessage>
            {errors.financeCommissionMenuCode?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderVehicleFromYear = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Vehicle Combination List Starting Year
          </label>
          <Controller
            control={control}
            name="vehicleFromYear"
            defaultValue={props.data?.vehicleFromYear}
            render={({ onChange, value, name }) => (
              <DropdownList
                data={years}
                placeholder="Model Year"
                onChange={(val) => onChange(val?.name?.toString())}
                name={name}
                value={value}
                textField="name"
                valueField="name"
              />
            )}
          />
        </div>
      </div>
    );
  };

  const renderApiEncryptionKey = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">API Encryption Key</label>
          <Controller
            control={control}
            rules={{
              required: 'API Encryption Key is required',
            }}
            name="apiEncryptionKey"
            defaultValue={props.data?.apiEncryptionKey}
            render={({ onChange, value, name }) => (
              <input
                type="password"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="API Encryption Key"
                name={name}
                value={value}
                onChange={onChange}
                id="apiEncryptionKey"
              />
            )}
          />
          <ErrorMessage>{errors.apiEncryptionKey?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderBaseUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Base Url</label>
          <Controller
            control={control}
            rules={{
              required: 'Base Url is required',
              validate: {
                validUrl: (value) => validator.isURL(value) || 'Invalid URL',
              },
            }}
            name="baseUrl"
            defaultValue={props.data?.baseUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Base Url"
                name={name}
                value={value}
                onChange={onChange}
                id="baseUrl"
              />
            )}
          />
          <ErrorMessage>{errors.baseUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderServiceDetailsBaseUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Service Details Base Url
          </label>
          <Controller
            control={control}
            rules={{
              validate: {
                validUrl: (value) =>
                  !value || validator.isURL(value) || 'Invalid URL',
              },
            }}
            name="serviceDetailsBaseUrl"
            defaultValue={props.data?.serviceDetailsBaseUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Base Url"
                name={name}
                value={value}
                onChange={onChange}
                id="serviceDetailsBaseUrl"
              />
            )}
          />
          <ErrorMessage>{errors.serviceDetailsBaseUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderServiceHistoryApiKey = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Service Details API Key
          </label>
          <Controller
            control={control}
            name="serviceDetailsApiKey"
            defaultValue={props.data?.serviceDetailsApiKey}
            render={({ onChange, value, name }) => (
              <input
                type="password"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Service Details API Key"
                name={name}
                value={value}
                onChange={onChange}
                id="serviceDetailsApiKey"
              />
            )}
          />
          <ErrorMessage>{errors.serviceDetailsApiKey?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderBrandName = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Brand Name</label>
          <Controller
            control={control}
            rules={{
              required: 'Brand Name is required',
            }}
            name="brandName"
            defaultValue={props.data?.brandName}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Brand Name"
                name={name}
                value={value}
                onChange={onChange}
                id="brandName"
              />
            )}
          />
          <ErrorMessage>{errors.brandName?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCustomerBrandName = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Customer Brand Name</label>
          <Controller
            control={control}
            rules={{
              required: 'Customer Brand Name is required',
            }}
            name="customerBrandName"
            defaultValue={props.data?.customerBrandName}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Customer Brand Name"
                name={name}
                value={value}
                onChange={onChange}
                id="customerBrandName"
              />
            )}
          />
          <ErrorMessage>{errors.customerBrandName?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            disabled={props.isSubmitting}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">Update Autoline Settings</h4>
        <div className="row gutter-10">
          {renderApiKey()}
          {renderBaseUrl()}
          {renderServiceDetailsBaseUrl()}
          {renderServiceHistoryApiKey()}
          {renderBrandName()}
          {renderCustomerBrandName()}
          {renderApiEncryptionKey()}
          {renderDefaultSalesmanHrSystemId()}
          {renderDefaultSourceOfBusiness()}
          {renderCampaignDiscountAutolineCode()}
          {renderAccessoriesDiscountAutolineCode()}
          {renderCashBackDiscountAutolineCode()}
          {renderCouponDiscountAutolineCode()}
          {renderAdjustmentsAutolineCode()}
          {renderListingPriceAutolineCode()}
          {renderFinanceCommissionMenuCode()}
          {renderVehicleFromYear()}
          {renderVinDiscountAutolineCode()}
          {renderExtraAddOnMenuCode()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
        </div>
      </div>
    </form>
  );
};

export default UpdateForm;
